import * as React from "react";
import { styled } from "@mui/material";
import { GroundingRag } from "../grounding/groundingRAG";
import { GroundedSummaryTextFragment } from "fe-common/graphql";
import {
  GuidelineIndicator,
  indicatorGuideline,
} from "../guidelines/GuidelineIndicator";
import { AnswerFooter, FooterHandle } from "../references/AnswerFooter";
import { AnnotatedText } from "../references/AnnotatedText";
import { useClinicState } from "ask/src/clinic/clinicStateContext";
import { isRtl } from "@kahunmedical/common-configuration";
import { ClinicConfigKeys } from "fe-common/graphql/clinic/clinicConfigKeys";
import { addAnnotations, rearrangeCitations } from "../references/utils";
import { useRef } from "react";
import { copyToClipboard } from "fe-common/utils/copyToClipboard";
import { useUserState } from "ask/src/components/diagnosticAgent/hooks/useUserState";
import { useClinicUser } from "ask/src/components/clinic/hooks/useClinicUser";
import { useClinicUserContext } from "ask/src/components/clinic/clinicUserContext";

//region [[ Styles ]]

const PatientAssessmentView = styled("div")({});

//endregion [[ Styles ]]

//region [[ Props ]]

export interface PatientAssessmentProps {
  assessment: GroundedSummaryTextFragment;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const PatientAssessment = ({
  assessment: {
    text: assessmentText,
    citations = [],
    grounding: groundingData,
    guidelines,
    altGuidelines,
  },
}: PatientAssessmentProps) => {
  const [{ clinicConfig }] = useClinicState();
  const { hasAdminAccess, hasSuperAccess } = useClinicUserContext();
  const footerRef = useRef<FooterHandle>(null);
  const withGrounding =
    clinicConfig?.[ClinicConfigKeys.GroundAssessment]?.value &&
    groundingData?.grounding;
  const assessmentRef = useRef(null);

  const rearrangedCitations = rearrangeCitations(citations);
  const indicatorGuidelines: indicatorGuideline[] =
    hasAdminAccess || hasSuperAccess
      ? guidelines
          ?.concat(altGuidelines || [])
          ?.map((g) => ({ ...g, disabled: true }))
      : guidelines;
  return (
    <PatientAssessmentView>
      {indicatorGuidelines && (
        <GuidelineIndicator
          guidelines={indicatorGuidelines}
          buttonStyle={{
            width: "auto",
            height: "auto",
            borderWidth: 1,
          }}
        />
      )}
      <div ref={assessmentRef}>
        {withGrounding ? (
          <GroundingRag groundedData={groundingData.grounding} />
        ) : (
          <AnnotatedText
            markdown={addAnnotations(
              assessmentText,
              rearrangedCitations?.footnotesMap,
            )}
            onFootnoteClick={(id) => {
              footerRef.current?.highlightRef(id);
            }}
            withValidationIcon
          />
        )}
      </div>
      {
        <AnswerFooter
          ref={footerRef}
          onCopy={() => {
            copyToClipboard(assessmentRef.current);
            return Promise.resolve();
          }}
          citations={rearrangedCitations?.citationsMap}
          rtlDisplay={isRtl(clinicConfig.defaultLocale)}
        />
      }
    </PatientAssessmentView>
  );
};
